import { makeStyles } from '@material-ui/styles'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import { BadgeVariant } from 'core/elements/badge/Badge'
import { createGridStatusCell, StatusCellModel } from 'core/elements/grid/cells/GridStatusCell'
import Grid, { GridViewColumn } from 'core/elements/grid/Grid'
import GridSearchFilter from 'core/elements/grid/GridSearchFilter'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import OverviewActions from 'core/elements/overview/OverviewActions'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import Theme from 'core/themes/model'
import ModalForm from 'pf9-ui-components/built/elements/modal/ModalForm'
import Text from 'pf9-ui-components/built/elements/Text'
import { keys, path } from 'ramda'
import React, { useEffect, useState } from 'react'
import { flavorsSelector } from '../../flavors/selectors'
import { listVirtualMachines } from '../../vms/actions'
import PowerVirtualMachineDialog from '../../vms/PowerVirtualMachineDialog'
import { makeGetvirtualMachinesByAppSelector } from '../../vms/selectors'
import { appLableMap, appStatus, vmsActions } from './helper'
import InfoBox from './InfoBox'

const useStyles = makeStyles<Theme>(() => ({
  formFieldSection: {
    marginBottom: '32px',
    border: 'none',
    padding: 0,
  },
  pageWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    display: 'grid',
    gap: 16,
    gridAutoRows: 'max-content',
  },
  contentContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  iconWrapper: {
    display: 'flex',
    height: '32px',
    width: '32px',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  customFormFieldSection: {
    '& .content': {
      marginLeft: '0px',
    },
  },
  iconsContainer: {
    display: 'flex',
    gap: 20,
    alignItems: 'center',
  },
  customButton: {
    '& button': {
      width: 36,
    },
    '& .button-text': {
      gap: 0,
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
    },
  },
  resourceContainer: {
    display: 'flex',
    alignItems: 'center',
  },
}))

type ActionParams = InferActionParams<typeof listVirtualMachines>

type Params = ActionParams & {}

const requiredParams: Array<keyof ActionParams> = []
const defaultParams: Params & SortingState = {
  orderBy: 'created',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'OpenstackVirtualMachines',
  listTablePrefs,
)

interface Props {
  data: any
  showVmsModal: boolean
  setShowVmsModal: (show: boolean) => void
}

type SelectorModel = ArrayElement<ReturnType<typeof flavorsSelector>>

const searchTargets = ['headerName']

const globalFilters = [
  {
    key: 'search',
    equalityComparerFn: (item, value) => {
      return !!searchTargets.find((key) =>
        String(
          Object.prototype.hasOwnProperty.call(item, key)
            ? item[String(key)]
            : path(String(key).split('.'), item),
        )
          .toLocaleLowerCase()
          .includes(value.toLocaleLowerCase()),
      )
    },
    FilterComponent: GridSearchFilter,
  },
]

const virtualMachinesByAppSelector = makeGetvirtualMachinesByAppSelector()
export default function VmsDetailsModal({ data, showVmsModal, setShowVmsModal }: Props) {
  const { allParams: params, setParams } = useGlobalParams(usePrefParams, defaultParams)
  const { loading, reload } = useListAction(listVirtualMachines, {
    params,
    requiredParams,
  })

  const vmsDetails = useSelectorWithParams(virtualMachinesByAppSelector, { appId: data?.uid }) || []
  const [selectedVms, setSelectedVms] = useState<SelectorModel[]>(null)
  const [selectedPowerAction, setSelectedPowerAction] = useState<string>('')
  const [showPowerDialog, setShowPowerDialog] = useState<boolean>(false)

  const vmColumns: GridViewColumn<SelectorModel>[] = [
    {
      key: 'name',
      label: 'Name',
    },
    {
      key: 'status',
      label: 'Status',
      CellComponent: ({ value, item }) =>
        createGridStatusCell({
          dataFn: (phase: string): StatusCellModel => {
            const variant: BadgeVariant = appStatus[phase] || 'unknown'
            let label = appLableMap[phase] || phase
            if (item?.taskState && item?.taskState !== 'Unknown') {
              label = `${label} (${item?.taskState})`
            }
            return { variant, label: label }
          },
        })({ value, item }),
    },
    {
      key: 'addresses',
      label: 'Available IPs',
      CellComponent: ({ value }) => {
        const ipAddressKeys = keys(value)
        return (
          <div className={classes.addresses}>
            {ipAddressKeys?.map((key: string) => {
              return (
                <div key={key}>
                  {/* <div>
                    <b>{key}</b>
                  </div> */}
                  {value[key]?.map((address) => (
                    <div key={address}>{address}</div>
                  ))}
                </div>
              )
            })}
          </div>
        )
      },
    },
    {
      key: 'flavor',
      label: 'Resources',
      CellComponent: ({ value, item }) => (
        <div className={classes.resourceContainer}>
          {value?.map((item, index) => (
            <InfoBox
              label={item?.label}
              value={item?.value}
              imagePath={item?.imagePath}
              showDivider={item?.showDivider}
            />
          ))}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      CellComponent: ({ value, item }) => (
        <div className={classes.iconsContainer}>
          <OverviewActions
            actions={vmsActions(item, reload)}
            entity={{}}
            className={classes.customButton}
            isIconOnly={true}
          />
        </div>
      ),
    },
  ]
  const imagePath = '/ui/images/add-member-logo.svg'
  const classes = useStyles()

  const handleClose = () => {
    setParams(defaultParams)
    setShowVmsModal(!showVmsModal)
  }
  const rowMenuItems: Array<GridRowMenuItemSpec<SelectorModel>> = [
    {
      label: 'Suspend',
      icon: 'pause',
      handleClick: (vm) => {
        setSelectedVms([vm])
        setSelectedPowerAction('suspend')
        setShowPowerDialog(true)
      },
      cond: (selected) => {
        return selected?.status === 'active'
      },
      disabledTooltip: 'VM must be in active state to suspend.',
      refreshAfterSuccess: true,
      hideIfDisabled: false,
    },
    {
      label: 'Resume',
      icon: 'play',
      handleClick: (vm) => {
        setSelectedVms([vm])
        setSelectedPowerAction('resume')
        setShowPowerDialog(true)
      },
      cond: (selected) => {
        return selected?.status === 'suspended'
      },
      disabledTooltip: 'VM must be in suspended state to resume.',
      refreshAfterSuccess: true,
      hideIfDisabled: false,
    },
  ]

  useEffect(() => {
    reload(true)
  }, [data?.uid])

  return (
    <>
      {showPowerDialog && (
        <PowerVirtualMachineDialog
          onClose={() => setShowPowerDialog(false)}
          rows={selectedVms}
          action={selectedPowerAction}
        />
      )}
      <ModalForm
        open={showVmsModal}
        title={`${data?.name} - VMs`}
        onClose={handleClose}
        //   error={error}
        cancelButtonLabel="Close"
        maxWidth={1090}
      >
        <>
          <div className={classes.formFieldSection}>
            <div className={classes.contentContainer}>
              <img className={classes.iconWrapper} alt="Load Balancer" src={imagePath} />
              <div className={classes.content}>
                <Text variant="body2">Manage the virtual machines for this application.</Text>
              </div>
            </div>
          </div>
          <FormFieldSection className={classes.customFormFieldSection} title="">
            <Grid<SelectorModel>
              uniqueIdentifier="id"
              columns={vmColumns}
              data={vmsDetails || []}
              label="Virtual Machines"
              orderBy="headerName"
              orderDirection="asc"
              emptyContent="No VMs available"
              disableRowSelection={true}
              loading={loading}
              onRefresh={reload}
              rowMenuItems={rowMenuItems}
              globalFilters={globalFilters}
              disableColumnHiding
            />
          </FormFieldSection>
        </>
      </ModalForm>
    </>
  )
}
